import { FirestoreProductSubject } from "reedy-data/api";

const WORD_SEPARATOR_RE = /[^\p{L}\p{N}]+/u;

/**
 * Split a keyword phrase into its lowercase word tokens.
 * Splits on anything that isn't a Unicode letter or digit, so punctuation
 * and whitespace both act as separators.
 */
export const phraseToWords = (phrase: string): string[] =>
  phrase.toLowerCase().split(WORD_SEPARATOR_RE).filter(Boolean);

/**
 * Compares two subjects by hotnessScore in Ascending order, with Nulls Last.
 * Used for column sorting definition.
 */
export const compareHotnessScore = (
  a: FirestoreProductSubject,
  b: FirestoreProductSubject,
) => {
  const scoreA = a.hotnessScore;
  const scoreB = b.hotnessScore;

  if (scoreA == null && scoreB == null) return 0;
  if (scoreA == null) return 1;
  if (scoreB == null) return -1;

  return Number(scoreA) - Number(scoreB);
};

export const findFinalMatch = (
  subject: FirestoreProductSubject,
  finalKeywords: FirestoreProductSubject[],
): FirestoreProductSubject | undefined => {
  const text = subject.subjectHeadingText?.trim().toLowerCase();
  if (text) {
    return finalKeywords.find(
      (kw) => kw.subjectHeadingText?.trim().toLowerCase() === text,
    );
  }
  return finalKeywords.find((kw) =>
    subject.subjectCode != null
      ? kw.subjectCode === subject.subjectCode
      : kw.id === subject.id,
  );
};

export const unifyKeywords = (
  databaseKeywords: FirestoreProductSubject[],
  generatedKeywords: FirestoreProductSubject[],
): FirestoreProductSubject[] => {
  const keywordMap = new Map<string, FirestoreProductSubject>();

  databaseKeywords.forEach((kw) => {
    if (kw.subjectHeadingText) {
      keywordMap.set(kw.subjectHeadingText.toLowerCase(), kw);
    }
  });

  generatedKeywords.forEach((kw) => {
    if (kw.subjectHeadingText) {
      const key = kw.subjectHeadingText.toLowerCase();
      if (!keywordMap.has(key)) {
        keywordMap.set(key, kw);
      }
    }
  });

  const keywords = Array.from(keywordMap.values());

  // Sort: Descending Score, but Nulls Last
  keywords.sort((a, b) => {
    const scoreA = a.hotnessScore;
    const scoreB = b.hotnessScore;

    // Handle nulls (always last)
    if (scoreA == null && scoreB == null) return 0;
    if (scoreA == null) return 1;
    if (scoreB == null) return -1;

    // Handle numbers (descending)
    return Number(scoreB) - Number(scoreA);
  });

  return keywords;
};
